import React from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import Logo from '@/components/brand/Logo'
import { ArrowRight, Check, Dumbbell, Apple, LineChart, Users } from 'lucide-react'

// Marketing styles follow the Revenuewise design system: slate neutrals,
// one blue-600 action color, 6px button radius, no gradients on controls.
const primaryCta = 'rounded-md bg-blue-600 text-white font-semibold shadow-none hover:bg-blue-700'
const secondaryCta = 'rounded-md border border-slate-300 bg-white text-slate-900 font-semibold shadow-none hover:bg-slate-50 hover:border-slate-400'
const focusRing = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 focus-visible:ring-0'
const navLink = 'text-sm font-medium text-slate-600 transition-colors hover:text-slate-900'

const Kicker = ({ children }) => (
  <p className="text-sm font-semibold uppercase tracking-[0.14em] text-blue-700">{children}</p>
)

// Soft blue panel that frames a slice of product UI, as on the Revenuewise site.
const UiPanel = ({ children }) => (
  <div className="rounded-3xl bg-gradient-to-br from-blue-50 to-indigo-100 p-5 sm:p-8">{children}</div>
)

const UiCard = ({ children, className = '' }) => (
  <div className={`rounded-xl border border-slate-200 bg-white shadow-[0_12px_30px_-18px_rgba(15,23,42,0.35)] ${className}`}>
    {children}
  </div>
)

const WorkoutSlice = () => {
  const exercises = [
    { name: 'Goblet squat', detail: '3 × 10 · 35 lb', done: true },
    { name: 'Romanian deadlift', detail: '3 × 8 · 95 lb', done: true },
    { name: 'Half-kneeling press', detail: '3 × 10 each side', done: false },
    { name: 'Farmer carry', detail: '4 × 40 yd', done: false },
  ]
  return (
    <UiCard className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[13px] font-semibold uppercase tracking-wide text-slate-500">Today</p>
          <p className="text-base font-semibold text-slate-900">Lower body strength</p>
        </div>
        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">2 of 4 done</span>
      </div>
      <ul className="mt-4 divide-y divide-slate-200 border-t border-slate-200">
        {exercises.map((exercise) => (
          <li key={exercise.name} className="flex items-center gap-3 py-3">
            <span
              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                exercise.done ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-300 bg-white'
              }`}
            >
              {exercise.done && <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />}
            </span>
            <span className="flex-1 text-sm font-medium text-slate-900">{exercise.name}</span>
            <span className="text-sm text-slate-500">{exercise.detail}</span>
          </li>
        ))}
      </ul>
    </UiCard>
  )
}

const NutritionSlice = () => {
  const macros = [
    { label: 'Protein', value: '128 / 160 g', pct: 80 },
    { label: 'Carbs', value: '182 / 220 g', pct: 83 },
    { label: 'Fat', value: '41 / 65 g', pct: 63 },
  ]
  return (
    <UiCard className="p-5">
      <p className="text-[13px] font-semibold uppercase tracking-wide text-slate-500">Today's macros</p>
      <p className="mt-1 text-3xl font-semibold text-slate-900">1,640 <span className="text-base font-normal text-slate-500">of 2,100 cal</span></p>
      <div className="mt-5 space-y-4">
        {macros.map((macro) => (
          <div key={macro.label}>
            <div className="flex justify-between text-sm">
              <span className="font-medium text-slate-700">{macro.label}</span>
              <span className="text-slate-500">{macro.value}</span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-slate-100">
              <div className="h-2 rounded-full bg-blue-500" style={{ width: `${macro.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    </UiCard>
  )
}

const ProgressSlice = () => {
  const bars = [42, 48, 46, 55, 58, 63, 70]
  return (
    <UiCard className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[13px] font-semibold uppercase tracking-wide text-slate-500">Lean mass</p>
          <p className="mt-1 text-3xl font-semibold text-slate-900">142.6 lb</p>
        </div>
        <span className="text-sm font-semibold text-emerald-700">+3.1 lb since March</span>
      </div>
      <div className="mt-6 flex h-28 items-end gap-2" aria-hidden="true">
        {bars.map((height, index) => (
          <div
            key={index}
            className={`flex-1 rounded-t ${index === bars.length - 1 ? 'bg-blue-500' : 'bg-blue-100'}`}
            style={{ height: `${height + 30}%` }}
          />
        ))}
      </div>
      <div className="mt-2 flex justify-between text-xs text-slate-500">
        <span>Mar</span>
        <span>Sep</span>
      </div>
    </UiCard>
  )
}

const ClientsSlice = () => {
  const clients = [
    { initials: 'JK', name: 'Jenna K.', note: 'Finished Week 3 · Lower body', status: 'On track', tone: 'bg-emerald-50 text-emerald-700' },
    { initials: 'MR', name: 'Marcus R.', note: 'No meals logged in 3 days', status: 'Check in', tone: 'bg-amber-50 text-amber-800' },
    { initials: 'PS', name: 'Priya S.', note: 'New body scan uploaded', status: 'Review', tone: 'bg-blue-50 text-blue-700' },
  ]
  return (
    <UiCard className="p-5">
      <p className="text-base font-semibold text-slate-900">Your clients</p>
      <ul className="mt-3 divide-y divide-slate-200 border-t border-slate-200">
        {clients.map((client) => (
          <li key={client.name} className="flex items-center gap-3 py-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-700">
              {client.initials}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-slate-900">{client.name}</span>
              <span className="block truncate text-sm text-slate-500">{client.note}</span>
            </span>
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${client.tone}`}>{client.status}</span>
          </li>
        ))}
      </ul>
    </UiCard>
  )
}

const stories = [
  {
    kicker: 'Workouts',
    title: 'Programs built in minutes.',
    body: 'Pick from the exercise library, set sets, reps and load, and assign the plan. Clients see exactly what to do today and check it off as they go.',
    visual: <WorkoutSlice />,
  },
  {
    kicker: 'Nutrition',
    title: 'Meals and macros, tracked.',
    body: 'Clients log meals against the targets you set. You see what they ate and where they’re falling short, without asking for a food diary.',
    visual: <NutritionSlice />,
  },
  {
    kicker: 'Progress',
    title: 'Progress they can see.',
    body: 'Body composition, measurements and check-ins over time, in charts clients understand. Proof the work is working.',
    visual: <ProgressSlice />,
  },
  {
    kicker: 'For coaches',
    title: 'Every client, one view.',
    body: 'See who’s on track and who needs a nudge. Keep notes, update plans and view the app exactly as your client does.',
    visual: <ClientsSlice />,
  },
]

const extras = [
  { icon: Dumbbell, title: 'Exercise library', body: 'Movements with instructions your clients can follow.' },
  { icon: Apple, title: 'Client nutrition', body: 'Targets, meal logs and history in one place.' },
  { icon: LineChart, title: 'Body composition', body: 'Scans and measurements, charted over time.' },
  { icon: Users, title: 'Your whole team', body: 'Consultants, clients and admins, each with the right view.' },
]

const steps = [
  { title: 'Set up your company', body: 'Add your coaches and invite your clients. Each person signs in to their own view.' },
  { title: 'Build the program', body: 'Create workout plans and nutrition targets from the library, then assign them to clients.' },
  { title: 'Coach the progress', body: 'Clients log workouts, meals and check-ins. You see it all and adjust as you go.' },
]

// Placeholder prices and limits: update before launch.
const TRIAL_DAYS = 14
const plans = [
  {
    name: 'Starter',
    blurb: 'For solo coaches getting started.',
    price: 29,
    features: ['Up to 10 clients', 'Workout plan builder', 'Exercise library', 'Client nutrition tracking'],
  },
  {
    name: 'Pro',
    blurb: 'For full-time coaches with a growing roster.',
    price: 79,
    featured: true,
    features: ['Up to 50 clients', 'Everything in Starter', 'Body composition tracking', 'Client notes and check-ins'],
  },
  {
    name: 'Team',
    blurb: 'For gyms and studios with several coaches.',
    price: 199,
    features: ['Unlimited clients', 'Everything in Pro', 'Multiple coaches', 'Admin tools and client view'],
  },
]

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 antialiased">
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/75">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className={`rounded-md ${focusRing}`} aria-label="HealthyCoach home">
            <Logo />
          </Link>
          <nav className="hidden items-center gap-8 md:flex" aria-label="Main">
            <a href="#features" className={navLink}>Features</a>
            <a href="#how-it-works" className={navLink}>How it works</a>
            <a href="#pricing" className={navLink}>Pricing</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/auth/login" className={`${navLink} rounded-md px-2 py-1 ${focusRing}`}>Log in</Link>
            <Button asChild size="sm" className={`h-9 px-4 ${primaryCta} ${focusRing}`}>
              <Link to="/auth/signup">Get started</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-[radial-gradient(70%_50%_at_50%_0%,#eff6ff_0%,transparent_70%)]">
          <div className="mx-auto max-w-6xl px-4 pb-16 pt-12 text-center sm:px-6 sm:pb-20 sm:pt-16">
            <p className="text-sm font-medium text-slate-500">For personal trainers, coaches and their clients</p>
            <h1 className="mx-auto mt-5 max-w-4xl text-balance text-5xl font-semibold leading-[1.02] tracking-tight text-slate-950 sm:text-7xl">
              Coach every client, all in one place.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-600 sm:text-xl">
              Workout plans, nutrition and progress tracking that you and your clients share. You build the program. They follow it. You both see it working.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button asChild className={`h-12 w-full px-6 text-base sm:w-auto ${primaryCta} ${focusRing}`}>
                <Link to="/auth/signup">
                  Get started
                  <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
              <Button asChild variant="outline" className={`h-12 w-full px-6 text-base sm:w-auto ${secondaryCta} ${focusRing}`}>
                <Link to="/auth/login">Log in</Link>
              </Button>
            </div>
          </div>
        </section>

        <section id="features" className="scroll-mt-16 py-16 sm:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-balance text-4xl font-semibold leading-[1.08] tracking-tight text-slate-950 sm:text-5xl">
                Everything your clients need between sessions.
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-slate-600 sm:text-xl">
                Training, nutrition and progress in one app, all working together.
              </p>
            </div>

            <div className="mt-12 space-y-12 sm:mt-16 sm:space-y-20">
              {stories.map((story, index) => (
                <div key={story.kicker} className="grid items-center gap-10 md:grid-cols-2 md:gap-16">
                  <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                    <Kicker>{story.kicker}</Kicker>
                    <h3 className="mt-3 text-3xl font-semibold leading-tight tracking-tight text-slate-950 sm:text-4xl">
                      {story.title}
                    </h3>
                    <p className="mt-4 text-lg leading-relaxed text-slate-600">{story.body}</p>
                  </div>
                  <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                    <UiPanel>{story.visual}</UiPanel>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-16 grid gap-8 border-t border-slate-200 pt-12 sm:mt-20 sm:grid-cols-2 lg:grid-cols-4">
              {extras.map((extra) => {
                const Icon = extra.icon
                return (
                  <div key={extra.title}>
                    <Icon className="h-6 w-6 text-blue-600" strokeWidth={1.75} aria-hidden="true" />
                    <p className="mt-3 text-base font-semibold text-slate-900">{extra.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{extra.body}</p>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        <section id="how-it-works" className="scroll-mt-16 bg-slate-50 py-16 sm:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <h2 className="text-center text-balance text-4xl font-semibold leading-[1.08] tracking-tight text-slate-950 sm:text-5xl">
              Up and running this week.
            </h2>
            <ol className="mt-10 grid gap-10 md:grid-cols-3">
              {steps.map((step, index) => (
                <li key={step.title}>
                  <span className="text-4xl font-semibold text-blue-600" aria-hidden="true">{index + 1}</span>
                  <p className="mt-3 text-base font-semibold text-slate-900">
                    <span className="sr-only">Step {index + 1}: </span>
                    {step.title}
                  </p>
                  <p className="mt-2 text-base leading-relaxed text-slate-600">{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="pricing" className="scroll-mt-16 py-16 sm:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-balance text-4xl font-semibold leading-[1.08] tracking-tight text-slate-950 sm:text-5xl">
                Simple pricing.
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-600">
                Every plan starts with a {TRIAL_DAYS}-day free trial.
              </p>
            </div>
            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {plans.map((plan) => (
                <div
                  key={plan.name}
                  className={`relative flex flex-col rounded-2xl border bg-white p-6 sm:p-8 ${
                    plan.featured
                      ? 'border-blue-600 ring-1 ring-blue-600 shadow-[0_28px_60px_-30px_rgba(37,99,235,0.6)]'
                      : 'border-slate-200'
                  }`}
                >
                  {plan.featured && (
                    <span className="absolute -top-3 left-6 rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white">
                      Most popular
                    </span>
                  )}
                  <h3 className="text-lg font-semibold text-slate-900">{plan.name}</h3>
                  <p className="mt-1 text-sm text-slate-600">{plan.blurb}</p>
                  <p className="mt-6 flex items-baseline gap-1">
                    <span className="text-5xl font-semibold tracking-tight text-slate-950">${plan.price}</span>
                    <span className="text-base text-slate-500">/mo</span>
                  </p>
                  <Button
                    asChild
                    variant={plan.featured ? 'default' : 'outline'}
                    className={`mt-6 h-11 w-full text-sm ${plan.featured ? primaryCta : secondaryCta} ${focusRing}`}
                  >
                    <Link to="/auth/signup">Start free trial</Link>
                  </Button>
                  <ul className="mt-8 space-y-3 border-t border-slate-200 pt-6 text-sm text-slate-700">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex gap-3">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" strokeWidth={2.5} aria-hidden="true" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-950 py-16 sm:py-20">
          <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
            <h2 className="text-balance text-4xl font-semibold leading-[1.08] tracking-tight text-white sm:text-5xl">
              Ready to coach smarter?
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-300">
              Set up your company, invite your clients and assign your first program today.
            </p>
            <Button asChild className={`mt-8 h-12 px-6 text-base rounded-md bg-white text-slate-900 font-semibold shadow-none hover:bg-slate-100 ${focusRing}`}>
              <Link to="/auth/signup">Get started</Link>
            </Button>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 sm:grid-cols-3 sm:px-6">
          <div>
            <Logo />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-600">
              Workouts, nutrition and progress for coaches and their clients.
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">Product</p>
            <ul className="mt-4 space-y-3 text-sm">
              <li><a href="#features" className={navLink}>Features</a></li>
              <li><a href="#how-it-works" className={navLink}>How it works</a></li>
              <li><a href="#pricing" className={navLink}>Pricing</a></li>
            </ul>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">Account</p>
            <ul className="mt-4 space-y-3 text-sm">
              <li><Link to="/auth/login" className={navLink}>Log in</Link></li>
              <li><Link to="/auth/signup" className={navLink}>Get started</Link></li>
              <li><Link to="/auth/forgot-password" className={navLink}>Reset password</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200">
          <p className="mx-auto max-w-6xl px-4 py-6 text-sm text-slate-500 sm:px-6">© {new Date().getFullYear()} HealthyCoach. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage
